import { PrismaEnums, Schedule } from '@airreading/schema/browser';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { assessmentFormatSchema } from './assessments.js';
import { classTimeTypeSchema } from './class-time-shared.js';
import { coachSchema } from './coach.js';
import { learningPathSchema, learningPathWithLessonsSchema, type LearningPathNames } from './learning-path.js';
import { lessonKindSchema, lessonSchema, lessonWithAssessmentsSchema } from './lesson.js';
import {
  DEPRECATED_pagingSchemaWithMaxPageSize,
  filteringSchemaFrom,
  largePagingSchema,
  pagingSchema,
  sortingSchemaFrom,
  sortOrderSchemaFrom,
} from './modifier.js';
import { videoConferenceTypeSchema } from './org-shared.js';
import { schoolWithOrgSchema } from './school.js';
import { onDemandGLAWithLessonSchema, plainSessionFeedbackSchema } from './session-feedbacks.js';
import { sessionGoogleMeetSchema } from './session-google-meet.js';
import {
  CoachSessionRemarkV2,
  coachSessionRemarkV2Schema,
  SessionStatus,
  sessionStatusSchema,
} from './session-shared.js';
import { SessionZoom, sessionZoomSchema } from './session-zoom.js';
import { studentGroupWithScheduleAndLevelSchema } from './student-group.js';
import { coachViewableStudentSchema, studentSchema, studentYearNullableSchema } from './student.js';
import {
  coachPortalSubstitutionRequestSchema,
  substitutionRequestSchema,
  substitutionRequestTypeSchema,
} from './substitution-request.js';
import { userLikeSchema, userSchema, userWithCoachProfileSchema } from './user.js';
import { paginatedResponse } from './util-schema-types.js';
export { CoachSessionRemarkV2, coachSessionRemarkV2Schema, SessionStatus, sessionStatusSchema };

export const { SessionAlert } = PrismaEnums;
export type SessionAlert = PrismaEnums.SessionAlert;
export const sessionAlertSchema = z.nativeEnum(SessionAlert);

export const meetingStatusSchema = z.enum(['waiting', 'started', 'ended', 'canceled']);
export type MeetingStatus = z.infer<typeof meetingStatusSchema>;
export const MeetingStatus = meetingStatusSchema.enum;
export const MeetingStatusLabelMap: Record<MeetingStatus, string> = {
  [MeetingStatus.waiting]: 'Waiting',
  [MeetingStatus.started]: 'In progress',
  [MeetingStatus.ended]: 'Ended',
  [MeetingStatus.canceled]: 'Canceled',
};

export const meetingProviderSchema = z.object({
  sessionId: z.string(),
  attendeeUrl: z.string().nullable(),
  hostUrl: z.string().nullable(),
  startedAt: z.array(z.instanceof(Temporal.Instant)),
  endedAt: z.array(z.instanceof(Temporal.Instant)),
  coachJoinAt: z.array(z.instanceof(Temporal.Instant)),
  coachLeaveAt: z.array(z.instanceof(Temporal.Instant)),
});
export type MeetingProvider = z.infer<typeof meetingProviderSchema>;

export const CoachSessionRemarkV2LabelMap: Record<CoachSessionRemarkV2, string> = {
  [CoachSessionRemarkV2.canceled]: 'Canceled',
  [CoachSessionRemarkV2.canceledWithin12Hours]: 'Canceled (within 12 hours)',
  [CoachSessionRemarkV2.subClass]: 'Sub class',
  [CoachSessionRemarkV2.subClassWithin12Hours]: 'Sub class (within 12 hours)',
  [CoachSessionRemarkV2.lateStart]: 'Late start',
  [CoachSessionRemarkV2.endEarly]: 'End early',
  [CoachSessionRemarkV2.tns]: 'Teacher no show',
};
export const CoachSessionRemarkV2CannotBeRemoved: CoachSessionRemarkV2[] = [
  CoachSessionRemarkV2.canceled,
  CoachSessionRemarkV2.canceledWithin12Hours,
];
export const CoachSessionRemarkV2CannotBeAdded: CoachSessionRemarkV2[] = [
  CoachSessionRemarkV2.canceled,
  CoachSessionRemarkV2.canceledWithin12Hours,
];

export const CoachSessionRemarkV2Completed: CoachSessionRemarkV2[] = [
  CoachSessionRemarkV2.endEarly,
  CoachSessionRemarkV2.subClass,
  CoachSessionRemarkV2.subClassWithin12Hours,
  CoachSessionRemarkV2.lateStart,
];

export function formatCoachSessionRemarkV2Label(
  tag: CoachSessionRemarkV2,
  session?: {
    zoom?: SessionZoom | null;
    startInstant?: Temporal.Instant;
    endInstant?: Temporal.Instant;
    addedCoachSessionRemarksV2?: CoachSessionRemarkV2[];
  },
) {
  const isManuallyAdded = session?.addedCoachSessionRemarksV2?.includes(tag) ?? false;

  if (tag === CoachSessionRemarkV2.lateStart) {
    if (!session?.zoom?.coachJoinAt[session?.zoom?.coachJoinAt.length - 1] || !session?.startInstant) {
      return CoachSessionRemarkV2LabelMap[CoachSessionRemarkV2.lateStart];
    }

    const hasLateStartMinutes = session.startInstant
      .until(session.zoom!.coachJoinAt[0])
      .round({ largestUnit: 'minutes' }).minutes;

    if (isManuallyAdded && hasLateStartMinutes === 0) {
      return CoachSessionRemarkV2LabelMap[CoachSessionRemarkV2.lateStart];
    }

    return `${CoachSessionRemarkV2LabelMap[CoachSessionRemarkV2.lateStart]} - ${hasLateStartMinutes} mins`;
  }

  if (tag === CoachSessionRemarkV2.endEarly) {
    if (!session?.endInstant || !session?.zoom?.coachLeaveAt[session?.zoom?.coachLeaveAt.length - 1]) {
      return CoachSessionRemarkV2LabelMap[CoachSessionRemarkV2.endEarly];
    }

    const endEarlyDuration = Temporal.Instant.from(session?.zoom!.coachLeaveAt[session?.zoom!.coachLeaveAt.length - 1])
      .until(session?.endInstant)
      .round({ largestUnit: 'minutes' }).minutes;

    if (isManuallyAdded && endEarlyDuration === 0) {
      return CoachSessionRemarkV2LabelMap[CoachSessionRemarkV2.endEarly];
    }

    return `${CoachSessionRemarkV2LabelMap[CoachSessionRemarkV2.endEarly]} - ${endEarlyDuration} mins`;
  }

  return CoachSessionRemarkV2LabelMap[tag];
}

export const sessionCompletionStatusSchema = z.enum(['Completed', 'In Progress', 'Incomplete', 'Not Started']);
export type SessionCompletionStatus = z.infer<typeof sessionCompletionStatusSchema>;
export const SessionCompletionStatus = sessionCompletionStatusSchema.enum;

export const meetingInfoPropsSchema = z.object({
  zoom: sessionZoomSchema.nullable(),
  googleMeet: sessionGoogleMeetSchema.nullable(),
  videoConferenceType: videoConferenceTypeSchema,
  endInstant: z.instanceof(Temporal.Instant),
  hoursCanceledBeforeStart: z.number().nullable(),
  status: sessionStatusSchema,
});
export type MeetingInfoProps = z.infer<typeof meetingInfoPropsSchema>;

export const meetingInfoSchema = z.object({
  kind: videoConferenceTypeSchema,
  status: meetingStatusSchema,
  startInstant: z.instanceof(Temporal.Instant).nullable(),
  endInstant: z.instanceof(Temporal.Instant).nullable(),
  provider: meetingProviderSchema.nullable(),
});
export type MeetingInfo = z.infer<typeof meetingInfoSchema>;

export const originalTeacherIssueSchema = z.enum([
  'tns_general_no_show',
  'regular_sub_request',
  'short_notice_sub_request',
  'regular_sub_request_excused',
  'short_notice_sub_request_excused',
  'tns_sub_requested',
  'tns_double_booking',
  'tns_teacher_emergency',
  'tns_left_early',
  'tns_scheduling_error',
  'tns_late_to_class',
  'tns_sub_no_show',
  'tns_wrong_link',
  'tit_general_end_early',
  'tit_internet_power_outage',
  'tit_zoom_meet_issue',
  'tit_sound_video',
  'tit_internet_connection_issue',
]);
export type OriginalTeacherIssue = z.infer<typeof originalTeacherIssueSchema>;
export const OriginalTeacherIssue = originalTeacherIssueSchema.enum;
export const OriginalTeacherIssueLabelMap: Record<OriginalTeacherIssue, string> = {
  [OriginalTeacherIssue.tns_general_no_show]: 'TNS: General (no show)',
  [OriginalTeacherIssue.regular_sub_request]: 'Sub request: Regular',
  [OriginalTeacherIssue.short_notice_sub_request]: 'Sub request: Short notice',
  [OriginalTeacherIssue.regular_sub_request_excused]: 'Sub request: Regular (excused)',
  [OriginalTeacherIssue.short_notice_sub_request_excused]: 'Sub request: Short notice (excused)',
  [OriginalTeacherIssue.tns_sub_requested]: 'TNS: Sub Requested',
  [OriginalTeacherIssue.tns_double_booking]: 'TNS: Double Booking',
  [OriginalTeacherIssue.tns_teacher_emergency]: 'TNS: Teacher Emergency',
  [OriginalTeacherIssue.tns_left_early]: 'TNS: Left Early',
  [OriginalTeacherIssue.tns_scheduling_error]: 'TNS: Scheduling Error',
  [OriginalTeacherIssue.tns_late_to_class]: 'TNS: Late to class',
  [OriginalTeacherIssue.tns_sub_no_show]: 'TNS: Sub no show',
  [OriginalTeacherIssue.tns_wrong_link]: 'TNS: Wrong link',
  [OriginalTeacherIssue.tit_general_end_early]: 'TIT: General (end early)',
  [OriginalTeacherIssue.tit_internet_power_outage]: 'TIT: Internet/power outage',
  [OriginalTeacherIssue.tit_zoom_meet_issue]: 'TIT: Zoom/Meet issue',
  [OriginalTeacherIssue.tit_sound_video]: 'TIT: Sound/video',
  [OriginalTeacherIssue.tit_internet_connection_issue]: 'TIT: Internet connection issue',
};
export const originalTeacherIssueSelectableSchema = originalTeacherIssueSchema.or(z.literal('null'));
export type OriginalTeacherIssueSelectable = z.infer<typeof originalTeacherIssueSelectableSchema>;
export const originalTeacherIssueSelectableValues: OriginalTeacherIssueSelectable[] = [
  'null',
  ...originalTeacherIssueSchema.options,
];

export const learningLossSchema = z.enum(['five_mins', 'five_to_ten_mins', 'ten_mins', 'never_started']);
export type LearningLoss = z.infer<typeof learningLossSchema>;
export const LearningLoss = learningLossSchema.enum;
export const LearningLossLabelMap: Record<LearningLoss, string> = {
  [LearningLoss.five_mins]: '5 mins',
  [LearningLoss.five_to_ten_mins]: '5-10 mins',
  [LearningLoss.ten_mins]: '10 mins',
  [LearningLoss.never_started]: 'Never started',
};
export const learningLossSelectableSchema = learningLossSchema.or(z.literal('null'));
export type LearningLossSelectable = z.infer<typeof learningLossSelectableSchema>;
export const learningLossSelectableValues: LearningLossSelectable[] = ['null', ...learningLossSchema.options];

function instantFromDate(date: Date): Temporal.Instant {
  return Temporal.Instant.fromEpochMilliseconds(+date);
}

export const sessionLikeSchema = z.object({
  start: z.date().transform(instantFromDate),
  end: z.date().transform(instantFromDate),
});
export type SessionLike = z.infer<typeof sessionLikeSchema>;

export { plainSessionLikeSchema } from './plain-session-like.js';
export type { PlainSessionLike } from './plain-session-like.js';

export const plainSessionSchema = z.object({
  id: z.string(),
  studentGroupId: z.string(),
  startInstant: z.instanceof(Temporal.Instant),
  endInstant: z.instanceof(Temporal.Instant),
  startPlainTime: z.string().nullable(),
  endPlainTime: z.string().nullable(),
  startPlainDate: z.string().nullable(),
  lessonId: z.string(),
  coachId: z.string().nullable(),
  status: sessionStatusSchema,
  alerts: z.array(sessionAlertSchema),
  isDelayed: z.boolean(),
  hourNotification: z.boolean(),
  videoConferenceType: videoConferenceTypeSchema,
  coachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
  addedCoachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
  removedCoachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
  originalTeacherIssue: originalTeacherIssueSchema.nullable(),
  originalTeacherIssueReason: z.string().nullable().optional(),
  originalTeacherIssueReasonDateInstant: z.instanceof(Temporal.Instant).nullable().optional(),
  learningLoss: learningLossSchema.nullable(),
  tnsNotes: z.string().nullable(),
  subNotes: z.string().nullable(),
  pinnedSubSession: z.boolean().nullable(),
});
export type PlainSession = z.infer<typeof plainSessionSchema>;

/** Coach-viewable student fields with optional Makeup Diagnostic "(Needs assessment)" label (teacher portal). */
export const coachSessionStudentSchema = z.lazy(() =>
  coachViewableStudentSchema.extend({
    needsDiagnosticAssessment: z.boolean().optional(),
  }),
);
export type CoachSessionStudent = z.infer<typeof coachSessionStudentSchema>;

export const coachSessionSchema = z.object({
  id: z.string(),
  startInstant: z.instanceof(Temporal.Instant),
  endInstant: z.instanceof(Temporal.Instant),
  startPlainTime: z.string().nullable(),
  endPlainTime: z.string().nullable(),
  startPlainDate: z.string().nullable(),
  status: sessionStatusSchema,
  coachId: z.string().nullable(),
  coach: z.lazy(() => userLikeSchema).nullable(),
  lesson: lessonWithAssessmentsSchema,
  substitutionRequests: z.array(substitutionRequestSchema),
  zoom: sessionZoomSchema.nullable(),
  googleMeet: sessionGoogleMeetSchema.nullable(),
  students: z.array(z.lazy(() => coachSessionStudentSchema)),
  schoolName: z.string().optional(),
  orgName: z.string().optional(),
  canceledAtInstant: z.instanceof(Temporal.Instant).nullable().optional(),
  hoursCanceledBeforeStart: z.number().nullable(),
  sessionFeedbacks: z.array(z.lazy(() => plainSessionFeedbackSchema)).optional(),
  meeting: meetingInfoSchema.nullable(),
  videoConferenceType: videoConferenceTypeSchema,
  lessonLink: z.string().nullable(),
  sessionCompletionStatus: sessionCompletionStatusSchema,
  coachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
  learningLoss: learningLossSchema.nullable(),
  tnsNotes: z.string().nullable(),
  subNotes: z.string().nullable(),
  onDemandGLAs: z.array(onDemandGLAWithLessonSchema),
});

export type CoachSession = z.infer<typeof coachSessionSchema>;

export const getCoachSessionsSortableColumnSchema = coachSessionSchema.keyof().extract(['startInstant']);

export type GetCoachSessionsSortableColumn = z.infer<typeof getCoachSessionsSortableColumnSchema>;

export const getCoachSessionsSortingSchema = sortingSchemaFrom(getCoachSessionsSortableColumnSchema);
export const getCoachSessionsSortOrderSchema = sortOrderSchemaFrom(getCoachSessionsSortableColumnSchema);

export type GetCoachSessionsSortOrder = z.infer<typeof getCoachSessionsSortOrderSchema>;

export const getCoachSessionsFilterSchema = z.object({});

export const getCoachSessionsOptionsSchema = DEPRECATED_pagingSchemaWithMaxPageSize
  .extend({
    startOnOrAfter: z.string().optional(),
    startOnOrBefore: z.string().optional(),
    endOnOrAfter: z.string().optional(),
    endOnOrBefore: z.string().optional(),
    query: z.string().optional(),
    showCanceled: z.string().optional(),
    sessionRemarksV2: z.string().optional(),
    includeSessionsWithoutSessionRemarksV2: z.literal('true').optional(),
  })
  .merge(getCoachSessionsSortingSchema);
export type GetCoachSessionsOptions = z.infer<typeof getCoachSessionsOptionsSchema>;

export const coachPortalSessionFeedbackStatusSchema = z.object({
  feedbackSubmitted: z.boolean(),
  lateFeedbackDismissed: z.boolean().nullable(),
  submittedAtInstant: z.instanceof(Temporal.Instant).nullable(),
  submittedHoursFromSessionEnd: z.number().nullable(),
});
export type CoachPortalSessionFeedbackStatus = z.infer<typeof coachPortalSessionFeedbackStatusSchema>;

/**
 * One pending/completed in-app timed reading assessment in a coach's session.
 */
export const coachPortalInAppAssessmentSchema = z.object({
  studentId: z.string(),
  assessmentResultId: z.string(),
  assessmentId: z.string(),
  assessmentContentId: z.string(),
  format: assessmentFormatSchema,
  assessmentFinished: z.boolean().nullable(),
  words: z.string().array(),
  readingPassage: z.string().nullable(),
  assessmentDisplayName: z.string(),
  assessmentContentName: z.string(),
});
export type CoachPortalInAppAssessment = z.infer<typeof coachPortalInAppAssessmentSchema>;

export const coachPortalSessionSchema = z.object({
  id: z.string(),
  startInstant: z.instanceof(Temporal.Instant),
  endInstant: z.instanceof(Temporal.Instant),
  status: sessionStatusSchema,
  coachId: z.string().nullable(),
  lesson: z.object({ name: z.string() }),
  lessonLink: z.string().nullable(),
  students: z.array(z.lazy(() => coachSessionStudentSchema)),
  onDemandGLAs: z.array(onDemandGLAWithLessonSchema),
  orgName: z.string().optional(),
  hoursCanceledBeforeStart: z.number().nullable(),
  videoConferenceType: videoConferenceTypeSchema,
  zoom: sessionZoomSchema.nullable(),
  googleMeet: sessionGoogleMeetSchema.nullable(),
  meeting: meetingInfoSchema.nullable(),
  sessionCompletionStatus: sessionCompletionStatusSchema,
  coachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
  substitutionRequests: z.array(coachPortalSubstitutionRequestSchema),
  sessionFeedbacks: z.array(coachPortalSessionFeedbackStatusSchema).optional(),
  inAppAssessments: z.array(coachPortalInAppAssessmentSchema).optional(),
});
export type CoachPortalSession = z.infer<typeof coachPortalSessionSchema>;

export const coachPortalSessionsResultsSchema = paginatedResponse.extend({
  coachSessions: z.array(coachPortalSessionSchema),
});
export type CoachPortalSessionsResults = z.infer<typeof coachPortalSessionsResultsSchema>;

export const studentSessionSchema = z.object({
  sessionId: z.string(),
  studentId: z.string(),
  startInstant: z.instanceof(Temporal.Instant),
  endInstant: z.instanceof(Temporal.Instant),
  status: sessionStatusSchema,
  coach: z.lazy(() => userLikeSchema).nullable(),
  lesson: lessonSchema,
  attendeeUrl: z.string().nullable(),
  substitutionRequests: z.array(substitutionRequestSchema),
  videoConferenceType: videoConferenceTypeSchema,
});

export type StudentSession = z.infer<typeof studentSessionSchema>;

export const studentSessionsResultsSchema = paginatedResponse.extend({
  studentSessions: z.array(studentSessionSchema),
});
export type StudentSessionsResults = z.infer<typeof studentSessionsResultsSchema>;

export const getStudentSessionsOptionsSchema = pagingSchema.extend({
  startOnOrAfter: z.string(),
});
export type GetStudentSessionsOptions = z.infer<typeof getStudentSessionsOptionsSchema>;

export const getAdminSessionsSortableColumnSchema = z.enum([
  'start',
  'googleMeetCheckedAt',
  'createdAt',
  'updatedAt',
  'lastManualUpdatedAt',
  'date',
  'time',
]);
export type GetAdminSessionsSortableColumn = z.infer<typeof getAdminSessionsSortableColumnSchema>;
const getAdminSessionsSortingSchema = sortingSchemaFrom(getAdminSessionsSortableColumnSchema);
export const getAdminSessionsSortOrderSchema = sortOrderSchemaFrom(getAdminSessionsSortableColumnSchema);
export type GetAdminSessionsSortOrder = z.infer<typeof getAdminSessionsSortOrderSchema>;

export const adminSessionSchema = z.object({
  id: z.string(),
  startInstant: z.instanceof(Temporal.Instant),
  endInstant: z.instanceof(Temporal.Instant),
  startPlainTime: z.string().nullable(),
  endPlainTime: z.string().nullable(),
  startPlainDate: z.string().nullable(),
  status: sessionStatusSchema,
  coachId: z.string().nullable(),
  coach: z.lazy(() => userWithCoachProfileSchema).nullable(),
  substituteCoachId: z.string().nullable(),
  substituteCoach: z.lazy(() => userWithCoachProfileSchema).nullable(),
  supportAdminId: z.string().nullable(),
  supportAdmin: z.lazy(() => userSchema).nullable(),
  lesson: lessonWithAssessmentsSchema,
  substitutionRequests: z.array(substitutionRequestSchema),
  zoom: sessionZoomSchema.nullable(),
  googleMeet: sessionGoogleMeetSchema.nullable(),
  students: z.array(z.lazy(() => userLikeSchema)),
  gradeByStudentId: z.record(z.string(), studentYearNullableSchema),
  isDelayed: z.boolean(),
  hourNotification: z.boolean(),
  alerts: z.array(sessionAlertSchema),
  studentGroupId: z.string(),
  school: schoolWithOrgSchema,
  meeting: meetingInfoSchema.nullable(),
  shouldDismissLateTag: z.boolean().nullable(),
  shouldDismissEndEarlyTag: z.boolean().nullable(),
  coachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
  addedCoachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
  removedCoachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
  originalTeacherIssue: originalTeacherIssueSchema.nullable(),
  originalTeacherIssueReason: z.string().nullable().optional(),
  originalTeacherIssueReasonDateInstant: z.instanceof(Temporal.Instant).nullable().optional(),
  joinedStudentsCount: z.number(),
  groupAverageAttendanceLast14Days: z.number().nullable(),
  sessionCompletionStatus: sessionCompletionStatusSchema,
  canceledAtInstant: z.instanceof(Temporal.Instant).nullable(),
  hoursCanceledBeforeStart: z.number().nullable(),
  videoConferenceType: videoConferenceTypeSchema,
  learningPath: learningPathSchema,
  lessonLink: z.string().nullable(),
  sessionFeedbacks: z.array(z.lazy(() => plainSessionFeedbackSchema)),
  learningLoss: learningLossSchema.nullable(),
  tnsNotes: z.string().nullable(),
  subNotes: z.string().nullable(),
  pinnedSubSession: z.boolean().nullable(),
  proposedTeacher: z
    .object({
      id: z.string(),
      fullNameOrEmail: z.string().nullable(),
    })
    .nullable(),
  createdAt: z.instanceof(Temporal.Instant),
  updatedAt: z.instanceof(Temporal.Instant),
  lastManualUpdatedAt: z.instanceof(Temporal.Instant).nullable(),
});

export type AdminSession = z.infer<typeof adminSessionSchema>;

export const adminSessionsResultsSchema = paginatedResponse.extend({
  adminSessions: z.array(adminSessionSchema),
});
export type AdminSessionsResults = z.infer<typeof adminSessionsResultsSchema>;

export const getAdminSessionsFilterSchema = z.object({
  startOnOrAfter: z.string().optional(),
  startOnOrBefore: z.string().optional(),
  endOnOrAfter: z.string().optional(),
  query: z.string().optional(),
  schools: z.array(z.string()).optional(),
  orgs: z.array(z.string()).optional(),
  grades: z.array(studentYearNullableSchema).optional(),
  lessons: z.array(z.string()).optional(),
  lessonKinds: z.array(lessonKindSchema).optional(),
  statuses: z.array(sessionStatusSchema).optional(),
  studentIds: z.array(z.string()).optional(),
  sessionRemarksV2: z.array(coachSessionRemarkV2Schema).optional(),
  originalTeacherIssues: z.array(originalTeacherIssueSchema).optional(),
  coachId: z.string().optional(),
  hasOpenSubRequest: z.boolean().optional(),
  attentionSessions: z.boolean().optional(),
  nextHour: z.boolean().optional(),
  supportAdminSessions: z.boolean().optional(),
  googleMeetSessions: z.boolean().optional(),
  hasCoachConflicts: z.boolean().optional(),
  classTypes: z.array(classTimeTypeSchema).optional(),
  studentGroupId: z.string().optional(),
  isDelayed: z.boolean().optional(),
  hourNotification: z.boolean().optional(),
  meetingStatuses: z.array(meetingStatusSchema).optional(),
});
export type GetAdminSessionsFilter = z.infer<typeof getAdminSessionsFilterSchema>;

export const getAdminSessionsOptionsSchema = largePagingSchema
  .merge(filteringSchemaFrom(getAdminSessionsFilterSchema))
  .merge(getAdminSessionsSortingSchema);
export type GetAdminSessionsOptions = z.infer<typeof getAdminSessionsOptionsSchema>;

export const SingleAdminSessionSchema = adminSessionSchema;

export type SingleAdminSession = z.infer<typeof SingleAdminSessionSchema>;

export const updateSessionOptionsSchema = z.object({
  status: z.nativeEnum(PrismaEnums.SessionStatus),
  lessonId: z.string().optional(),
  start: z.string().optional(),
  end: z.string().optional(),
  zoom: z
    .object({
      hostUrl: z.string().nullable().optional(),
      attendeeUrl: z.string().nullable().optional(),
      zoomUuid: z.string().nullable().optional(),
    })
    .optional(),
  googleMeet: z
    .object({
      hostUrl: z.string().nullable().optional(),
      attendeeUrl: z.string().nullable().optional(),
    })
    .optional(),
  videoConferenceType: videoConferenceTypeSchema.optional(),
  shouldSendEmail: z.boolean().optional(),
  subNotes: z.string().nullable().optional(),
  learningLoss: learningLossSchema.nullable().optional(),
  shouldDismissLateTag: z.boolean().optional(),
  shouldDismissEndEarlyTag: z.boolean().optional(),
  coachSessionRemarksV2: z.array(coachSessionRemarkV2Schema).optional(),
  addedCoachSessionRemarksV2: z.array(coachSessionRemarkV2Schema).optional(),
  removedCoachSessionRemarksV2: z.array(coachSessionRemarkV2Schema).optional(),
  originalTeacherIssue: originalTeacherIssueSchema.nullable().optional(),
  originalTeacherIssueReason: z.string().nullable().optional(),
  supportAdminId: z.string().nullable().optional(),
});
export type UpdateSessionOptions = z.infer<typeof updateSessionOptionsSchema>;

export const updateBulkSessionsOptionsSchema = z.object({
  sessionIds: z.array(z.string()),
  status: z.nativeEnum(PrismaEnums.SessionStatus),
});
export type UpdateBulkSessionsOptions = z.infer<typeof updateBulkSessionsOptionsSchema>;

export const bulkRefreshZoomLinksOptionsSchema = z.object({
  sessionIds: z.array(z.string()),
});
export type BulkRefreshZoomLinksOptions = z.infer<typeof bulkRefreshZoomLinksOptionsSchema>;

export const updateSessionStudentsOptionsSchema = z.object({
  studentIds: z.array(z.string()),
  shouldSendUpdateConfirmationEmail: z.boolean().optional(),
});
export type UpdateSessionStudentsOptions = z.infer<typeof updateSessionStudentsOptionsSchema>;

export const updatePinnedSubSessionOptionsSchema = z.object({
  pinnedSubSession: z.boolean().nullable(),
});
export type UpdatePinnedSubSessionOptions = z.infer<typeof updatePinnedSubSessionOptionsSchema>;

export const updateSessionOriginalTeacherOptionsSchema = z.object({
  coachId: z.string().uuid(),
  sessionRemark: z.enum([CoachSessionRemarkV2.subClass, CoachSessionRemarkV2.subClassWithin12Hours]).optional(),
});
export type UpdateSessionOriginalTeacherOptions = z.infer<typeof updateSessionOriginalTeacherOptionsSchema>;

export const sessionTeacherChangeActionSchema = z.enum([
  'open_pending',
  'sub_and_fulfill',
  'fulfill',
  'cancel_pending',
]);
export type SessionTeacherChangeAction = z.infer<typeof sessionTeacherChangeActionSchema>;

const updateSessionTeacherSharedFieldsSchema = {
  sendConfirmationEmail: z.boolean().optional().default(false),
  originalTeacherIssue: originalTeacherIssueSchema.nullable().optional(),
  originalTeacherIssueReason: z.string().nullable().optional(),
};

export const updateSessionTeacherOptionsSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('open_pending'),
    requestedByCoachId: z.string().uuid(),
    type: substitutionRequestTypeSchema,
    requestReason: z.string().optional(),
    ...updateSessionTeacherSharedFieldsSchema,
  }),
  z.object({
    action: z.literal('cancel_pending'),
    ...updateSessionTeacherSharedFieldsSchema,
  }),
  z.object({
    action: z.literal('fulfill'),
    newSessionCoachId: z.string().uuid(),
    ...updateSessionTeacherSharedFieldsSchema,
  }),
  z.object({
    action: z.literal('sub_and_fulfill'),
    newSessionCoachId: z.string().uuid(),
    requestedByCoachId: z.string().uuid(),
    /**
     * Optional fulfill timestamp when recreating session with a previous confirmed sub.
     * When omitted, fulfill uses `Temporal.Now`.
     */
    acceptedAtInstant: z.custom<Temporal.Instant>((value) => value instanceof Temporal.Instant).optional(),
    /**
     * Optional create timestamp when recreating session with a previous confirmed sub.
     */
    createdAtInstant: z.custom<Temporal.Instant>((value) => value instanceof Temporal.Instant).optional(),
    ...updateSessionTeacherSharedFieldsSchema,
  }),
]);
export type UpdateSessionTeacherOptions = z.infer<typeof updateSessionTeacherOptionsSchema>;

export const createSessionsForStudentGroupOptionsSchema = z.object({
  overrideStartingLessonId: z.string().optional(),
  startOnOrAfter: z.string().optional(),
  endOnOrBefore: z.string().optional(),
  coachId: z.string().optional(),
  coach: z.lazy(() => coachSchema).optional(),
  students: z.array(z.lazy(() => studentSchema)).optional(),
  learningPath: learningPathWithLessonsSchema.optional(),
});

export const sessionCoachRemarksV2PayloadSchema = z.object({
  coachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
  addedCoachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
  removedCoachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
});

export type SessionCoachRemarksV2Payload = z.infer<typeof sessionCoachRemarksV2PayloadSchema>;

export type UpdateBulkSessionsResults = {
  errors: string[];
  successSessionIds: string[];
  failedSessionIds: string[];
};

export type CreateSessionsForStudentGroupOptions = z.infer<typeof createSessionsForStudentGroupOptionsSchema>;

export type CreateSessionsForGroupsResults = {
  errors: string[];
  successGroupIds: string[];
  failedGroupIds: string[];
};

export const backfillSessionRemindersSkippedReasonSchema = z.enum([
  'no_teaching_coach',
  'start_in_past',
  'teaching_coach_name_not_found',
  'other',
]);
export type BackfillSessionRemindersSkippedReason = z.infer<typeof backfillSessionRemindersSkippedReasonSchema>;

export const backfillSessionRemindersSkippedItemSchema = z.object({
  sessionId: z.string(),
  reason: backfillSessionRemindersSkippedReasonSchema,
  detail: z.string().optional(),
});
export type BackfillSessionRemindersSkippedItem = z.infer<typeof backfillSessionRemindersSkippedItemSchema>;

export const backfillSessionRemindersResultSchema = z.object({
  success: z.boolean(),
  sessionsAdded: z.array(z.string()),
  sessionsSkipped: z.array(backfillSessionRemindersSkippedItemSchema),
  errors: z.array(z.string()).optional(),
  /** Present and true when the request was dry-run (no events were fired). */
  dryRun: z.literal(true).optional(),
  summary: z.object({
    total: z.number(),
    added: z.number(),
    skipped: z.number(),
  }),
});
export type BackfillSessionRemindersResult = z.infer<typeof backfillSessionRemindersResultSchema>;

export const getSessionsForStudentGroupOptionsSchema = z.object({
  statuses: z.string().optional(),
});
export type GetSessionsForStudentGroupOptions = z.infer<typeof getSessionsForStudentGroupOptionsSchema>;

export const createSessionsForStudentGroupsOptionsSchema = z.object({
  studentGroupIds: z.array(z.string()),
  overrideStartingLessonId: z.string().optional(),
  shouldSendEmail: z.boolean().default(false).optional(),
});
export type CreateSessionsForStudentGroupsOptions = z.infer<typeof createSessionsForStudentGroupsOptionsSchema>;

export const createManualSessionForStudentGroupOptionsSchema = z.object({
  coachId: z.string(),
  studentIds: z.array(z.string()),
  start: z.string(),
  end: z.string(),
  lessonId: z.string(),
});
export type CreateManualSessionForStudentGroupOptions = z.infer<typeof createManualSessionForStudentGroupOptionsSchema>;

export const bulkCreateManualSessionForStudentGroupsOptionsSchema = z.object({
  studentGroupIds: z.array(z.string()),
  start: z.string(),
  end: z.string(),
  lessonId: z.string(),
});
export type BulkCreateManualSessionForStudentGroupsOptions = z.infer<
  typeof bulkCreateManualSessionForStudentGroupsOptionsSchema
>;

export const getStudentUpcomingSessionsPaginatedOptionsSchema = pagingSchema.extend({
  startOnOrAfter: z.string().optional(),
});
export type GetStudentUpcomingSessionsPaginatedOptions = z.infer<
  typeof getStudentUpcomingSessionsPaginatedOptionsSchema
>;

export const updateFutureSessionsOptionsSchema = z.object({
  studentGroupId: z.string(),
  startOnOrAfter: z.string(),
  overrideStartingLessonId: z.string(),
});
export type UpdateFutureSessionsOptions = z.infer<typeof updateFutureSessionsOptionsSchema>;

export const onboardingSessionTypeSchema = z.enum([
  'practice',
  'practice_google_meet',
  'diagnostic',
  'practice_and_diagnostic',
  'practice_and_diagnostic_google_meet',
  'makeup_diagnostic',
]);
export type OnboardingSessionType = z.infer<typeof onboardingSessionTypeSchema>;

export type OnboardingLessonName =
  | 'Diagnostic'
  | 'Practice (Zoom)'
  | 'Practice (Google Meet)'
  | 'Diagnostic w/ Practice (Zoom)'
  | 'Diagnostic w/ Practice (Google Meet)'
  | 'Makeup Diagnostic 1-1';

export type OnboardingLearningPathName = Extract<
  LearningPathNames,
  | 'Diagnostic'
  | 'Practice (Zoom)'
  | 'Practice (Google Meet)'
  | 'Diagnostic w/ Practice (Zoom)'
  | 'Diagnostic w/ Practice (Google Meet)'
  | 'Makeup Diagnostic'
>;

export type OnboardingSessionConfig = {
  lessonName: OnboardingLessonName;
  learningPathName: OnboardingLearningPathName;
  lessonType: OnboardingSessionType;
  groupSize: number;
  scheduleDateKey: Extract<keyof Schedule, 'practicePlainDate' | 'diagnosticPlainDate'>;
  maxSessionsByRange?: number;
};

export const createClassesAndOnboardingSessionOptionsSchema = z.object({
  studentIds: z.array(z.string()),
  lessonId: z.string(),
  learningPathId: z.string(),
  groupSize: z.number(),
  startPlainTime: z.instanceof(Temporal.PlainTime),
  endPlainTime: z.instanceof(Temporal.PlainTime),
  startPlainDate: z.instanceof(Temporal.PlainDate),
  scheduleId: z.string(),
  autoCreateSessions: z.boolean(),
  type: onboardingSessionTypeSchema,
  orgId: z.string(),
  autoCreateMakeupSessions: z.boolean().optional(),
  makeupStartPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  makeupEndPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  prioritizeTeachersWithoutClasses: z.boolean().optional(),
});
export type CreateClassesAndOnboardingSessionOptions = z.infer<typeof createClassesAndOnboardingSessionOptionsSchema>;

/** Shown when class creation succeeds but sessions are skipped for missing original teacher. */
export const MISSING_ORIGINAL_TEACHER_SESSION_GENERATION_MESSAGE =
  'Sessions could not be generated because the class does not have an Original teacher assigned.';

/** Shown when bulk session generation/updates are blocked for missing original teacher. */
export const MISSING_ORIGINAL_TEACHER_SESSION_ACTION_MESSAGE =
  'Cannot perform this action because one or more selected classes do not have an Original teacher assigned.';

export const createClassesAndOnboardingSessionResultsSchema = z.object({
  studentGroupsCount: z.number(),
  sessionsCount: z.number(),
  availableCoachesCount: z.number(),
  coachesAssignedCount: z.number(),
  sessionGenerationWarnings: z.array(z.string()).optional(),
});
export type CreateClassesAndOnboardingSessionResults = z.infer<typeof createClassesAndOnboardingSessionResultsSchema>;

export const createClassesAndRegularSessionsOptionsSchema = z.object({
  studentIds: z.array(z.string()),
  groupSize: z.number(),
  classTimeId: z.string(),
  autoCreateSessions: z.boolean(),
  overrideStartingLevel: z
    .object({
      learningPathId: z.string(),
      startingLessonId: z.string(),
    })
    .optional(),
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  overrideEndPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  orgId: z.string(),
  prioritizeTeachersWithoutClasses: z.boolean().optional(),
});
export type CreateClassesAndRegularSessionsOptions = z.infer<typeof createClassesAndRegularSessionsOptionsSchema>;
export const createClassesAndRegularSessionsResultsSchema = z.object({
  studentGroupsCount: z.number(),
  sessionsCount: z.number(),
  availableCoachesCount: z.number(),
  coachesAssignedCount: z.number(),
  sessionGenerationWarnings: z.array(z.string()).optional(),
});
export type CreateClassesAndRegularSessionsResults = z.infer<typeof createClassesAndRegularSessionsResultsSchema>;

export const createSessionOptionsSchema = z.object({
  coachId: z.string(),
  studentIds: z.array(z.string()),
  start: z.string(),
  end: z.string(),
  lessonId: z.string(),
  studentGroupId: z.string(),
});
export type CreateSessionOptions = z.infer<typeof createSessionOptionsSchema>;

// Lightweight sessions for notification jobs
export const notificationSessionLikeSchema = z.object({
  id: z.string(),
  startInstant: z.instanceof(Temporal.Instant),
  endInstant: z.instanceof(Temporal.Instant),
  coachId: z.string().nullable(),
  substituteCoachId: z.string().nullable(),
  lessonName: z.string(),
  zoomHostUrl: z.string().nullable(),
  studentNamesOrEmails: z.array(z.string().nullable()),
});
export type NotificationSessionLike = z.infer<typeof notificationSessionLikeSchema>;

export const getNotificationSessionsOptionsSchema = z.object({
  startOnOrAfter: z.string(),
  startOnOrBefore: z.string(),
  statuses: z.array(sessionStatusSchema),
  canSendEmailSMS: z.boolean().optional(),
});
export type GetNotificationSessionsOptions = z.infer<typeof getNotificationSessionsOptionsSchema>;

export const scheduledClassWithStudentGroupSchema = plainSessionSchema.extend({
  studentGroup: studentGroupWithScheduleAndLevelSchema,
  sessionsLeft: z.number(),
});
export type ScheduledClassWithStudentGroup = z.infer<typeof scheduledClassWithStudentGroupSchema>;

export const getSessionTeachersByStudentGroupOptionsSchema = z.object({
  studentGroupId: z.string().uuid(),
});
export type GetSessionTeachersByStudentGroupOptions = z.infer<typeof getSessionTeachersByStudentGroupOptionsSchema>;

export function uniqueLessonStandardsByKeyAcrossSessions(
  sessions: AdminSession[],
  standardKey: 'floridaStandard' | 'arkansasStandard',
): string {
  const standards = sessions.flatMap((session) =>
    (session.lesson[standardKey] ?? '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean),
  );

  return [...new Set(standards)].join(', ');
}

export function uniqueSessionsLessonsStandardsByState(
  sessions: AdminSession[],
  standardKey: 'floridaStandard' | 'arkansasStandard',
): string {
  return uniqueLessonStandardsByKeyAcrossSessions(sessions, standardKey);
}

/**
 * Like {@link uniqueSessionsLessonsStandardsByState}, but only includes sessions where
 * `studentId` has submitted feedback with `studentAttended === true`. Sessions with no
 * feedback or with absent/null attendance do not contribute standards.
 */
export function uniqueSessionsLessonsStandardsByStateForAttendedSessions(
  sessions: AdminSession[],
  studentId: string,
  standardKey: 'floridaStandard' | 'arkansasStandard',
): string {
  const attendedSessions = sessions.filter((session) => {
    const feedback = session.sessionFeedbacks.find((f) => f.studentId === studentId);
    return feedback?.studentAttended === true;
  });
  return uniqueLessonStandardsByKeyAcrossSessions(attendedSessions, standardKey);
}
